import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Utensils } from "lucide-react";
import { FoodSearchPanel } from "@/components/site/FoodSearchPanel";

const today = () => new Date().toLocaleDateString("en-CA");

export const FoodTracker = ({ api, clientId }) => {
  const [date, setDate] = useState(today());
  const [tracker, setTracker] = useState(null);

  const loadTracker = () =>
    api().get(`/admin/diet/clients/${clientId}/tracker?date=${date}`).then((r) => setTracker(r.data)).catch(() => {});

  useEffect(() => { loadTracker(); }, [clientId, date]); // eslint-disable-line

  const search = async (q) => {
    const r = await api().get(`/admin/foods/search?q=${encodeURIComponent(q)}`);
    return r.data.results;
  };

  const add = async (item) => {
    try {
      await api().post(`/admin/diet/clients/${clientId}/tracker`, { ...item, date });
      toast.success(`${item.name} (${item.serving}) added to ${date}`);
      loadTracker();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const remove = async (id) => {
    try { await api().delete(`/admin/diet/tracker/${id}`); loadTracker(); } catch { toast.error("Could not remove"); }
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5 mt-5" data-testid="food-tracker">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="font-medium flex items-center gap-2"><Utensils size={15} className="text-[#89937C]" /> Food Search & Daily Tracker</div>
        <input type="date" data-testid="tracker-date" value={date} onChange={(e) => setDate(e.target.value)}
          className="bg-[#F7F5F0] border border-[#E5E2D9] rounded-xl h-9 px-3 text-xs" />
      </div>

      <FoodSearchPanel search={search} onAdd={add} />

      <div className="mt-4 pt-3 border-t border-[#F0EEE6]">
        <div className="text-xs font-semibold text-[#5C665A] mb-2">Tracked on {date}</div>
        {!tracker ? <Loader2 size={14} className="animate-spin text-[#89937C]" /> : tracker.items.length === 0 ? (
          <p className="text-xs text-[#9AA294]" data-testid="tracker-empty">Nothing tracked for this day yet.</p>
        ) : (
          <>
            <ul className="space-y-1" data-testid="tracker-items">
              {tracker.items.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-2 text-xs text-[#5C665A]">
                  <span className="truncate">
                    {i.name}{i.serving ? ` (${i.serving})` : ""} — {i.calories} kcal · P {i.protein}g · C {i.carbs}g · F {i.fat}g
                    {i.added_by === "client" && <span className="ml-1 rounded-full bg-[#89937C]/15 text-[#5C665A] text-[9px] font-semibold px-1.5 py-0.5">client</span>}
                  </span>
                  <button data-testid={`tracker-del-${i.id}`} onClick={() => remove(i.id)} className="shrink-0 text-[#A05252] hover:opacity-70"><Trash2 size={12} /></button>
                </li>
              ))}
            </ul>
            <div className="mt-2 rounded-xl bg-[#F7F5F0] px-3 py-2 text-xs font-semibold" data-testid="tracker-totals">
              Day total: {tracker.totals.calories} kcal · P {tracker.totals.protein}g · C {tracker.totals.carbs}g · F {tracker.totals.fat}g
            </div>
          </>
        )}
      </div>
    </div>
  );
};
